import { create } from 'zustand';
import { PersistOptions, persist } from 'zustand/middleware';

import { StorageNames } from '@/lib/constants';
import { AddressPrequalification } from '@/services/prequal/service';
import { T_OrderDetails } from '@/types';
import { T_AcquisitionForm } from '@/types';

interface AcquisitionFormState {
  formData: Partial<T_AcquisitionForm>;
  guestPrequalData: AddressPrequalification | null;
  orderDetails: T_OrderDetails | null;
  showChangingProvider?: boolean;
  setFormData: (data: Partial<T_AcquisitionForm>) => void;
  setGuestPrequal: (prequal: AddressPrequalification | null) => void;
  setOrderDetails: (order: T_OrderDetails) => void;
  setShowChangingProvider?: (value: boolean) => void;
  clearAcquisitionFormStore: (isReset: boolean, isAddressReset?: boolean) => void;
  acquisitionBackup: any;
  acquisitionBackUpStore: () => void;
  restoreAcquisitionStore: () => void;
  resetForm?: (values?: any) => void;
  setResetForm: (fn: (values?: any) => void) => void;
  clearEmail: () => void; // Optional method to clear email
}

type AcquisitionBackupState = Omit<
  AcquisitionFormState,
  'acquisitionBackUpStore' | 'restoreAcquisitionStore' | 'clearEmail'
>;

type PersistAcquisitionFormState = Pick<AcquisitionFormState, 'formData' | 'guestPrequalData'>;

const persistOptions: PersistOptions<AcquisitionFormState, PersistAcquisitionFormState> = {
  name: StorageNames.ACQUISITION_FORM_STORAGE, // name of the item in the storage (must be unique)
  //   storage: createJSONStorage(() => localStorage), // (optional) by default, 'localStorage' is used
  // allow only specific state to be persist in storage
  partialize: (state: AcquisitionFormState) => ({
    formData: state.formData,
    guestPrequalData: state.guestPrequalData,
    orderDetails: state.orderDetails,
  }),
  // Rehydrate logic with Zustand's onRehydrateStorage
  onRehydrateStorage: () => state => {
    if (state?.guestPrequalData) {
      // Re-instantiate AddressPrequalification
      state.guestPrequalData = new AddressPrequalification(state.guestPrequalData?.prequal);
    }
    // Convert the string back to a Date object
    //if (state?.formData.email) state.formData.email = '';

    if (state?.formData?.dateOfBirth)
      state.formData.dateOfBirth = new Date(state.formData.dateOfBirth);
    // @ts-ignore
    if (state?.formData?.installationDate)
      // @ts-ignore
      state.formData.installationDate = state?.formData?.installationDate
        ? new Date(state?.formData?.installationDate)
        : '';
    if (state?.formData?.prefferedInstallationDate)
      // @ts-ignore
      state.formData.prefferedInstallationDate = state?.formData?.prefferedInstallationDate
        ? new Date(state?.formData?.prefferedInstallationDate)
        : undefined;
  },
};

const initialFormData: Partial<T_AcquisitionForm> = {
  email: '',
  firstName: '',
  lastName: '',
  areaCode: '',
  phoneNumber: '',
  // @ts-ignore
  // installationDate: subWeeks(new Date(), -2),
  abandonedCartAck: false,
  paymentMethod: '',
  consent: false,
  read: false,
  accept: false,
  accountCheck: false,
  bankName: '',
  accountNumber: '',
  serviceAddress: {
    label: '',
    id: '',
    tui: '',
  },
  tui: '',
  residencyStatus: 0,
  isUsingExistingPhone: 'Yes',
  isKeepExistingPhoneNumber: 'No',
  isPhoneProviderMatchBroadband: 'No',
  saveModelAck: false,
  hardwareDeliveryDialog: 'No',
  isCheckoutStarted: false,
  additionalNotes: '',
  deliveryInstructions: '',
  alternateName: '',
  alternatePhoneAreaCode: '',
  alternatePhoneLineNumber: '',
  dogsAtHome: '',
  whoIsAtHome: '',
};

const initialAcquisitionFormState = {
  formData: initialFormData,
  guestPrequalData: null,
  orderDetails: null,
  acquisitionBackup: null as AcquisitionBackupState | null,
  resetForm: undefined,
  // showChangingProvider: false,
};

export const useAcquisitionFormStore = create<AcquisitionFormState>()(
  persist(
    (set, get) => ({
      ...initialAcquisitionFormState,
      setFormData: data => set(state => ({ formData: { ...state.formData, ...data } })),
      setGuestPrequal: prequal => set({ guestPrequalData: prequal }),
      setOrderDetails: order => set({ orderDetails: order }),
      // setShowChangingProvider: value => set({ showChangingProvider: value }),
      clearAcquisitionFormStore: (isReset: boolean, isAddressReset?: boolean) => {
        if (isReset) {
          set({ ...initialAcquisitionFormState });
        } else {
          set(state => ({
            ...initialAcquisitionFormState,
            formData: {
              ...initialAcquisitionFormState.formData,
              firstName: state.formData.firstName,
              lastName: state.formData.lastName,
              email: state.formData.email,
              phoneNumber: state.formData.phoneNumber,
              dateOfBirth: state.formData.dateOfBirth,
              serviceAddress: isAddressReset
                ? state.formData.serviceAddress
                : initialAcquisitionFormState.formData.serviceAddress,
            },
          }));
        }
      },

      setResetForm: fn => set({ resetForm: fn }),

      // Only call this when Auth0 login fails
      clearEmail: () =>
        set(state => ({
          formData: {
            ...state.formData,
            email: '',
          },
        })),

      // Backup current state (exclude  acquisitionBackup to prevent circular references)
      acquisitionBackUpStore: () => {
        const state = get();
        const { acquisitionBackup, ...backupState } = state; // Exclude  acquisitionBackup itself
        set({
          acquisitionBackup: { ...backupState }, // Store the backup excluding the dynamic parts
        });
      },

      // Restore state from backup
      restoreAcquisitionStore: () => {
        const backupState = get().acquisitionBackup;
        if (backupState) {
          set({ ...backupState, acquisitionBackup: null });
        }
      },
    }),
    persistOptions
  )
);
